import React from 'react';
import { motion } from 'framer-motion';
import { BeakerIcon, CpuChipIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';

const FEATURE_TITLE = "Clinical Microbiology Analysis";
const FEATURE_DESCRIPTION = "Automated detection and identification of pathogens from laboratory samples with classification by type and detailed clinical context.";

const ClinicalMicrobiologyPage: React.FC = () => {
  return (
    <div className="py-16 bg-background">
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h1 
            className="text-4xl font-extrabold text-text tracking-tight sm:text-5xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {FEATURE_TITLE}
          </motion.h1>
          <motion.p 
            className="mt-4 text-xl text-text max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {FEATURE_DESCRIPTION}
          </motion.p>
        </div>

        <div className="mt-16">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <BeakerIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Pathogen Identification</h3>
              <p className="text-text">
                Our system rapidly identifies pathogens from laboratory samples using advanced machine learning algorithms, 
                providing accurate results with detailed classification by type.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">98.7% accuracy in identifying common pathogens</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Broad database covering 2,500+ bacterial species</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Rapid results - typically within minutes</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.5 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <CpuChipIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Advanced Analytics</h3>
              <p className="text-text">
                Our platform processes complex microbiology data to extract meaningful patterns and clinical relevance,
                helping healthcare providers make informed decisions quickly.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">AI-powered analysis of culture results</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Pattern recognition across patient populations</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Trend forecasting for infection control</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.7 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <DocumentTextIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Clinical Context</h3>
              <p className="text-text">
                We don't just identify pathogens—we provide comprehensive clinical context to help understand 
                the significance of findings in relation to patient health.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Integration with patient history</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Severity assessment based on clinical variables</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Literature-based relevance scoring</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>

        <motion.div 
          className="mt-16 bg-section-light p-8 rounded-lg shadow-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <h2 className="text-2xl font-bold text-text mb-4">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center">
              <div className="h-12 w-12 rounded-full bg-primary text-text-light flex items-center justify-center mb-4">
                <span className="font-bold">1</span>
              </div>
              <h3 className="text-lg font-semibold text-text mb-2">Sample Upload</h3>
              <p className="text-text text-center">Upload lab results directly or connect to your LIS for automatic processing.</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="h-12 w-12 rounded-full bg-primary text-text-light flex items-center justify-center mb-4">
                <span className="font-bold">2</span>
              </div>
              <h3 className="text-lg font-semibold text-text mb-2">AI Analysis</h3>
              <p className="text-text text-center">Our proprietary algorithms process and analyze the data within seconds.</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="h-12 w-12 rounded-full bg-primary text-text-light flex items-center justify-center mb-4">
                <span className="font-bold">3</span>
              </div>
              <h3 className="text-lg font-semibold text-text mb-2">Clinical Report</h3>
              <p className="text-text text-center">Receive a comprehensive report with actionable insights.</p>
            </div>
          </div>
        </motion.div>

        <div className="mt-16 text-center">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              href="/contact"
              variant="primary"
              size="lg"
            >
              Schedule a Demo
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ClinicalMicrobiologyPage; 